/**
 * OnboardingTooltip  —  UI layer
 *
 * Pure rendering component. All positioning and animation logic lives in
 * useOnboardingTooltip (script) → useTooltipAnimation (animation control).
 */

import {
  AlignLeft,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  MapPin,
  X,
} from 'lucide-react';
import { useState } from 'react';

import type { GuideStep } from '../config/types';
import { useOnboardingTooltip } from '../hooks/useOnboardingTooltip';

import { OnboardingProgress } from './OnboardingProgress';
import type { SpotlightRect } from './SpotlightOverlay';

type OnboardingTooltipProps = {
  step: GuideStep;
  currentStep: number;
  totalSteps: number;
  rect: SpotlightRect | null;
  /** False when the current step belongs to a different page than the user is on. */
  isOnCorrectPage: boolean;
  /** Human-readable name of the page the user needs to navigate to. */
  requiredPageLabel?: string;
  onNext: () => void;
  onPrev: () => void;
  /** Close the tooltip and advance one step — guide is NOT marked as seen. */
  onPause: () => void;
  /** Permanently dismiss the guide and mark it as seen. */
  onSkip: () => void;
  onComplete: () => void;
};

export const OnboardingTooltip = ({
  step,
  currentStep,
  totalSteps,
  rect,
  isOnCorrectPage,
  requiredPageLabel,
  onNext,
  onPrev,
  onPause,
  onSkip,
  onComplete,
}: OnboardingTooltipProps) => {
  const {
    cardRef,
    cardStyle,
    isFirst,
    isLast,
    startDismiss,
    handleTransitionEnd,
  } = useOnboardingTooltip({ step, rect, currentStep, totalSteps, onPause });

  const [showMedia, setShowMedia] = useState(false);

  return (
    <div
      ref={cardRef}
      style={cardStyle}
      className="relative bg-gray-900 text-white rounded-xl shadow-2xl border border-gray-700/60 p-5 select-none"
      onClick={(e) => e.stopPropagation()}
      onTransitionEnd={handleTransitionEnd}
    >
      {/* ── Close button ───────────────────────────────────── */}
      <button
        type="button"
        onClick={startDismiss}
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-300 transition-colors focus:outline-none"
        title="Close — resume later from next step"
      >
        <X className="size-4" />
      </button>

      {/* ── Header row ─────────────────────────────────────── */}
      <div className="flex items-center mb-3">
        {totalSteps > 1 && (
          <span className="text-xs font-semibold text-blue-400 uppercase tracking-widest">
            Step {currentStep + 1} of {totalSteps}
          </span>
        )}
      </div>

      {/* ── Title + media toggle ───────────────────────────── */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <h3 className="text-[15px] font-semibold leading-snug">{step.title}</h3>
        {step.media && (
          <div className="flex shrink-0 rounded-md border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowMedia(false)}
              className={`p-1 transition-colors focus:outline-none ${!showMedia ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
              title="Show description"
            >
              <AlignLeft className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setShowMedia(true)}
              className={`p-1 transition-colors focus:outline-none ${showMedia ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
              title="Show image"
            >
              <ImageIcon className="size-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* ── Description / Media (one at a time) ────────────── */}
      {showMedia && step.media?.kind === 'image' ? (
        <img
          src={step.media.src}
          alt={step.media.alt ?? step.title}
          className="mb-4 w-full rounded-lg object-contain"
        />
      ) : (
        <div
          className="text-sm text-gray-300 leading-relaxed mb-4 [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-1 [&_p]:mb-2 last:[&_p]:mb-0"
          dangerouslySetInnerHTML={{ __html: step.description }}
        />
      )}

      {/* ── Progress dots ──────────────────────────────────── */}
      <OnboardingProgress current={currentStep} total={totalSteps} />

      {/* ── Navigation ─────────────────────────────────────── */}
      <div className="flex items-center justify-between mt-2">
        {!step.hideBack && (
          <button
            type="button"
            onClick={onPrev}
            disabled={isFirst}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus:outline-none"
          >
            <ChevronLeft className="size-4" />
            Back
          </button>
        )}

        {/* ── Page-gate: user is on the wrong page for this step ── */}
        {!isOnCorrectPage ? (
          <div className="flex items-center gap-1.5 text-xs text-amber-400/80">
            <MapPin className="size-3.5 shrink-0" />
            <span>Navigate to {requiredPageLabel}</span>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={onSkip}
              className="text-xs text-gray-600 hover:text-gray-400 transition-colors focus:outline-none"
            >
              Skip guide
            </button>

            {!step.hideNext &&
              (isLast ? (
                <button
                  type="button"
                  onClick={onComplete}
                  className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-sm font-medium rounded-lg transition-colors focus:outline-none"
                >
                  {totalSteps === 1 ? 'Got it' : 'Done'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onNext}
                  className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-sm font-medium rounded-lg transition-colors focus:outline-none"
                >
                  Next
                  <ChevronRight className="size-4" />
                </button>
              ))}
          </>
        )}
      </div>
    </div>
  );
};
